import { type Opportunity, type PartnerOrganization } from "@entities";
import type { PostRegistrationContent } from "@shared/helpers/post-registration-content";
import {
  DEFAULT_REGISTRATION_BUTTON_TEXT,
  normalizeRegistrationButtonTextForStorage,
  normalizePreRegistrationProcessTextForStorage,
  PRE_REGISTRATION_PROCESS_TEXT_MAX_LENGTH,
  REGISTRATION_BUTTON_TEXT_MAX_LENGTH,
} from "@shared/helpers/constants";
import {
  VolunteerRegistrationOutcome,
  resolveVolunteerRegistrationFlowForAdmin,
} from "@shared/helpers/volunteer-registration-outcome";
import {
  ADMIN_GROUP_CANNOT_DISABLE,
  ADMIN_GROUP_TOGGLE_LABEL,
  ADMIN_TABLE_PARTNER_VIA_PARENT,
} from "@shared/helpers/opportunity-group-admin-copy";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  OpportunityVolunteerMode,
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  resolveOpportunityVolunteerMode,
  type OpportunityVolunteerModeType,
} from "@shared/helpers/opportunity-volunteer-mode";
import { opportunityRepo, partnerOrganizationRepo } from "@repositories";
import { useQuery } from "@tanstack/react-query";
import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from "react";
import { AgePicker } from "../../components/add-request/AgePicker";
import { GenderPicker } from "../../components/add-request/GenderPicker";
import { TwoCheckbox } from "../../components/two-checkbox/TwoCheckbox";
import { CategorySelect } from "../../components/add-request/CategorySelect";
import { PopulationSelect } from "../../components/add-request/PopulationSelect";
import { UrgencySelect } from "../../components/add-request/UrgencySelect";
import { useForm } from "../../helpers/forms/FormProvider";
import { PartnerOrganizationSelect } from "../../components/partner-organization-select/PartnerOrganizationSelect";
import { OpportunityFormSection } from "./OpportunityFormSection";

function paddedPostRegLinks(
  content: PostRegistrationContent | undefined,
): { label: string; url: string; }[] {
  const links = content?.links ?? [];
  return [0, 1, 2].map((i) => ({
    label: links[i]?.label ?? "",
    url: links[i]?.url ?? "",
  }));
}

function buildPostRegistrationState(
  text: string | undefined,
  padded: { label: string; url: string; }[],
): PostRegistrationContent | undefined {
  const textVal = text !== undefined && text.length > 0 ? text : undefined;
  const linksNonEmpty = padded
    .map((l) => ({ label: l.label, url: l.url }))
    .filter((l) => l.url.trim().length > 0);
  const links = linksNonEmpty.length > 0 ? linksNonEmpty : undefined;
  if (!textVal && !links?.length) return undefined;
  return { text: textVal, links };
}

const VOLUNTEER_REGISTRATION_FLOW_OPTIONS: {
  value: Opportunity["volunteerRegistrationFlow"];
  label: string;
}[] = [
    { value: VolunteerRegistrationOutcome.SMS_DETAILS, label: "הרשמה ופרטי מבקש ב-SMS" },
    { value: VolunteerRegistrationOutcome.REGULAR, label: "הרשמה רגילה" },
    { value: VolunteerRegistrationOutcome.INTERESTED, label: "התעניינות (ממתין לאישור)" },
    { value: VolunteerRegistrationOutcome.CHAT, label: "הרשמה וצ'אט עם מבקש הסיוע" },
  ];

const RequestMoreDetails: FC<{
  setRequestDetails: Dispatch<SetStateAction<Partial<Opportunity>>>;
  requestDetails?: Partial<Opportunity>;
  isGroupEvent?: boolean;
}> = ({ setRequestDetails, requestDetails, isGroupEvent = false }) => {
  const { values, setValues, setTouched } = useForm<Partial<Opportunity>>();
  const { data: groupChildCount = 0 } = useQuery({
    queryKey: ["group-parent-child-count", requestDetails?.id],
    queryFn: () =>
      opportunityRepo.count({
        parentOpportunityId: requestDetails!.id!,
        isDeleted: false,
      }),
    enabled: Boolean(
      requestDetails?.id && requestDetails?.isOpportunityGroupParent,
    ),
  });
  const markExtraDetailsTouched = () => {
    setTouched((t) => ({ ...t, minAge: true }));
  };

  const partnerOrgId = requestDetails?.partnerOrganizationId ?? null;
  const includedPartnerOrgId = requestDetails?.partnerOrganization?.id;
  const isGroupChild = Boolean(requestDetails?.parentOpportunityId);
  const [selectedPartnerOrg, setSelectedPartnerOrg] =
    useState<PartnerOrganization | null>(
      requestDetails?.partnerOrganization ?? null,
    );

  useEffect(() => {
    const included = requestDetails?.partnerOrganization;
    if (included?.id && included.id === partnerOrgId) {
      setSelectedPartnerOrg((prev) =>
        prev?.id === included.id ? prev : included,
      );
      return;
    }
    if (!partnerOrgId) {
      setSelectedPartnerOrg(null);
      return;
    }
    let cancelled = false;
    void partnerOrganizationRepo.findId(partnerOrgId).then((org) => {
      if (cancelled || !org) return;
      setSelectedPartnerOrg(org);
    });
    return () => {
      cancelled = true;
    };
  }, [partnerOrgId, includedPartnerOrgId]);

  const postRegDisplayLinks = paddedPostRegLinks(
    requestDetails?.postRegistrationContent,
  );

  return (
    <Stack gap={2} width="100%">
      <OpportunityFormSection title="זיהוי וסיווג">
        {isGroupChild ? (
          <Typography variant="body2" color="text.secondary">
            {ADMIN_TABLE_PARTNER_VIA_PARENT}
          </Typography>
        ) : (
          <PartnerOrganizationSelect
            value={selectedPartnerOrg}
            onChange={(org) => {
              setSelectedPartnerOrg(org);
              setRequestDetails((prev) => ({
                ...prev,
                partnerOrganizationId: org?.id ?? null,
              }));
              setTouched((t) => ({ ...t, partnerOrganizationId: true }));
            }}
          />
        )}
        <CategorySelect />
        <PopulationSelect />
        <UrgencySelect />
      </OpportunityFormSection>

      <OpportunityFormSection title="מאפייני משימה">
        <TwoCheckbox
          labelClassName="more-details-label"
          isChecked={!!values?.isRemote || false}
          handleChange={(event) => {
            setValues((draft) => {
              draft.isRemote = event.target.name === "yes" ? true : false;
            });
          }}
          text="משימה מרחוק"
        />
        <TwoCheckbox
          labelClassName="more-details-label"
          isChecked={!!requestDetails?.isLongTerm || false}
          handleChange={(event) => {
            markExtraDetailsTouched();
            setRequestDetails((prev) => ({
              ...prev,
              isLongTerm: event.target.name === "yes",
            }));
          }}
          text="התנדבות ממושכת (ארוכת טווח)"
        />
        <Stack gap={1}>
          <Typography variant="body2">סוג מתנדבים</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={resolveOpportunityVolunteerMode(
              requestDetails?.volunteerMode,
            )}
            onChange={(_, value: OpportunityVolunteerModeType | null) => {
              if (!value) return;
              markExtraDetailsTouched();
              setRequestDetails((prev) => ({
                ...prev,
                volunteerMode: value,
              }));
            }}
            sx={{ "& .MuiToggleButton-root": { textTransform: "none" } }}
          >
            {(
              Object.values(
                OpportunityVolunteerMode,
              ) as OpportunityVolunteerModeType[]
            ).map((mode) => (
              <ToggleButton key={mode} value={mode}>
                {OPPORTUNITY_VOLUNTEER_MODE_HEB[mode]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </OpportunityFormSection>

      <OpportunityFormSection title="תהליך הרשמה">
        <FormControl fullWidth size="small">
          <InputLabel id="volunteer-registration-flow-label">תהליך הרשמה</InputLabel>
          <Select
            labelId="volunteer-registration-flow-label"
            label="תהליך הרשמה"
            value={resolveVolunteerRegistrationFlowForAdmin(requestDetails)}
            onChange={(e) => {
              markExtraDetailsTouched();
              setRequestDetails((prev) => ({
                ...prev,
                volunteerRegistrationFlow: e.target.value as Opportunity["volunteerRegistrationFlow"],
              }));
            }}
          >
            {VOLUNTEER_REGISTRATION_FLOW_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="טקסט כפתור הרשמה"
          placeholder={DEFAULT_REGISTRATION_BUTTON_TEXT}
          value={requestDetails?.registrationButtonText ?? ""}
          onChange={(e) => {
            markExtraDetailsTouched();
            const value = e.target.value;
            setRequestDetails((prev) => ({
              ...prev,
              registrationButtonText: value.length > 0 ? value : undefined,
            }));
          }}
          onBlur={(e) => {
            const normalized = normalizeRegistrationButtonTextForStorage(
              e.target.value,
            );
            setRequestDetails((prev) => ({
              ...prev,
              registrationButtonText: normalized,
            }));
          }}
          inputProps={{ maxLength: REGISTRATION_BUTTON_TEXT_MAX_LENGTH }}
          helperText="טקסט מותאם אישית לכפתור ההרשמה. השאר ריק לברירת המחדל."
          fullWidth
          size="small"
        />
        <TextField
          label="טקסט תהליך הרשמה (לפני ההרשמה)"
          multiline
          minRows={3}
          value={requestDetails?.preRegistrationProcessText ?? ""}
          onChange={(e) => {
            markExtraDetailsTouched();
            const value = e.target.value;
            setRequestDetails((prev) => ({
              ...prev,
              preRegistrationProcessText:
                value.length > 0 ? value : undefined,
            }));
          }}
          onBlur={(e) => {
            setRequestDetails((prev) => ({
              ...prev,
              preRegistrationProcessText:
                normalizePreRegistrationProcessTextForStorage(e.target.value),
            }));
          }}
          inputProps={{ maxLength: PRE_REGISTRATION_PROCESS_TEXT_MAX_LENGTH }}
          helperText="מוצג בכרטיסייה לפני לחיצה על כפתור ההרשמה."
          fullWidth
          size="small"
        />
      </OpportunityFormSection>

      <OpportunityFormSection title="נראות">
        <TwoCheckbox
          labelClassName="more-details-label"
          isChecked={!!requestDetails?.isOpportunityGroupParent || false}
          handleChange={(event) => {
            if (requestDetails?.isRepeated) return;
            if (
              event.target.name === "no" &&
              groupChildCount > 0
            ) {
              return;
            }
            markExtraDetailsTouched();
            setRequestDetails((prev) => ({
              ...prev,
              isOpportunityGroupParent: event.target.name === "yes",
              ...(event.target.name === "yes" ? { isHidden: false } : {}),
            }));
          }}
          text={ADMIN_GROUP_TOGGLE_LABEL}
        />
        {groupChildCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {ADMIN_GROUP_CANNOT_DISABLE} ({groupChildCount})
          </Typography>
        )}
        {!requestDetails?.isOpportunityGroupParent && (
          <TwoCheckbox
            labelClassName="more-details-label"
            isChecked={!!requestDetails?.isHidden || false}
            handleChange={(event) => {
              markExtraDetailsTouched();
              setRequestDetails((prev) => ({
                ...prev,
                isHidden: event.target.name === "yes",
              }));
            }}
            text="מוסתר ממתנדבים (לא יופיע בפיד)"
          />
        )}
      </OpportunityFormSection>

      <OpportunityFormSection
        title="תוכן אחרי הרשמה"
        description="טקסט ועד 3 לינקים שיוצגו בדרואר אחרי הרשמה"
      >
        <TextField
          label="טקסט"
          multiline
          minRows={2}
          value={requestDetails?.postRegistrationContent?.text ?? ""}
          onChange={(e) => {
            markExtraDetailsTouched();
            const text = e.target.value;
            setRequestDetails((prev) => {
              const padded = paddedPostRegLinks(prev.postRegistrationContent);
              return {
                ...prev,
                postRegistrationContent: buildPostRegistrationState(
                  text.length > 0 ? text : undefined,
                  padded,
                ),
              };
            });
          }}
          fullWidth
          size="small"
        />
        {[0, 1, 2].map((i) => (
          <Stack key={i} direction="row" gap={1} alignItems="center">
            <TextField
              label={`לינק ${i + 1} – כותרת`}
              value={postRegDisplayLinks[i]?.label ?? ""}
              onChange={(e) => {
                markExtraDetailsTouched();
                const label = e.target.value;
                setRequestDetails((prev) => {
                  const padded = paddedPostRegLinks(prev.postRegistrationContent);
                  padded[i] = { ...padded[i], label };
                  return {
                    ...prev,
                    postRegistrationContent: buildPostRegistrationState(
                      prev.postRegistrationContent?.text,
                      padded,
                    ),
                  };
                });
              }}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="כתובת"
              placeholder="https://"
              value={postRegDisplayLinks[i]?.url ?? ""}
              onChange={(e) => {
                markExtraDetailsTouched();
                const url = e.target.value;
                setRequestDetails((prev) => {
                  const padded = paddedPostRegLinks(prev.postRegistrationContent);
                  padded[i] = { ...padded[i], url };
                  return {
                    ...prev,
                    postRegistrationContent: buildPostRegistrationState(
                      prev.postRegistrationContent?.text,
                      padded,
                    ),
                  };
                });
              }}
              size="small"
              sx={{ flex: 1 }}
            />
          </Stack>
        ))}
      </OpportunityFormSection>

      <OpportunityFormSection title="קהל יעד">
        <GenderPicker />
        <AgePicker />
      </OpportunityFormSection>
    </Stack>
  );
};

export default RequestMoreDetails;
